import { createCollection } from '@tanstack/react-db';
import type { LoadSubsetOptions } from '@tanstack/react-db';
import type { SimpleComparison, ParsedOrderBy } from '@tanstack/query-db-collection';
import { queryCollectionOptions, parseLoadSubsetOptions } from '@tanstack/query-db-collection';
import { queryClient } from '@/layouts/root';
import { getWorkspaceIdFromCurrentUrl } from '@/context/current-workspace';

export type IdValue = string | number;

export interface ParsedPredicates {
  filters: SimpleComparison[];
  sorts: ParsedOrderBy[];
  limit?: number;
}

/**
 * Create a workspace-scoped, on-demand collection.
 *
 * TanStack DB pushes query predicates (where, orderBy, limit) to your
 * `queryFn`. You map them to your API's query params however you need.
 *
 * @example
 * ```ts
 * export const list = createOnDemandCollection<Campaign>({
 *   id: 'campaigns',
 *   getKey: (item) => item.id,
 *   queryFn: async (workspaceId, predicates) => {
 *     const stateFilter = predicates.filters.find(f => f.field[0] === 'state');
 *     const data = await api.getCampaigns({
 *       params: { environment_id: workspaceId },
 *       query: { state: stateFilter?.value as string },
 *     });
 *     return data.campaigns;
 *   },
 * });
 * ```
 */
export function createOnDemandCollection<T extends object>(options: {
  id: string;
  getKey: (item: T) => IdValue;
  queryFn: (workspaceId: number, predicates: ParsedPredicates) => Promise<T[]>;
  staleTime?: number;
}) {
  return createCollection(
    queryCollectionOptions<T>({
      id: options.id,
      queryKey: (options_: LoadSubsetOptions) => buildOnDemandKey(options.id, options_),
      queryClient,
      getKey: options.getKey,
      syncMode: 'on-demand',
      staleTime: options.staleTime,
      queryFn: async (context) => {
        const workspaceId = getWorkspaceIdFromCurrentUrl();
        if (workspaceId == null || workspaceId <= 0) return [];
        const predicates = parseLoadSubsetOptions(context.meta?.loadSubsetOptions);
        return options.queryFn(workspaceId, predicates);
      },
    }),
  );
}

/** @internal Exported for testing */
export function buildOnDemandKey(prefix: string, options: LoadSubsetOptions): unknown[] {
  const workspaceId = getWorkspaceIdFromCurrentUrl();
  const parsed = parseLoadSubsetOptions(options);
  const key: unknown[] = [prefix, workspaceId];
  for (const f of parsed.filters) {
    key.push(`${f.field.join('.')}-${f.operator}-${f.value}`);
  }
  for (const s of parsed.sorts) {
    key.push(`sort:${s.field.join('.')}-${s.direction}`);
  }
  if (parsed.limit != null) {
    key.push(`limit:${parsed.limit}`);
  }
  return key;
}

/** Helper: find a filter by field name. */
export function findFilter(predicates: ParsedPredicates, fieldName: string): SimpleComparison | undefined {
  return predicates.filters.find((f) => f.field.join('.') === fieldName);
}
